import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSeo } from "@/hooks/useSeo";
import { news } from "@/mocks/news";

const PAGE_SIZE = 4;

export default function News() {
  useSeo({
    title: "影视资讯 - 一本道影视大全",
    description: "一本道影视资讯频道，汇集最新行业动态、影视快讯、票房与奖项信息，第一时间了解影视圈发生的事。",
    keywords: "影视资讯,影视新闻,行业动态,票房,影视快讯",
  });

  const sorted = [...news].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const [featured, ...rest] = pageItems;

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-3">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">影视资讯</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">影视资讯</h1>
          <p className="mt-3 text-sm text-foreground-500 max-w-2xl">
            关注影视行业最新动态，掌握票房、奖项、制作与平台的一手资讯，让每一次浏览都紧跟潮流。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 rounded-lg border border-dashed border-background-300">
            <span className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600">
              <i className="ri-newspaper-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">暂无资讯内容</h3>
            <p className="mt-2 text-sm text-foreground-500">我们正在持续更新，敬请期待</p>
          </div>
        ) : (
          <>
            {featured && (
              <Link
                to={`/news/detail/${featured.id}`}
                className="group block rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer mb-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="w-full aspect-[16/10] md:aspect-auto md:h-full overflow-hidden bg-background-200">
                    <img
                      src={featured.cover}
                      alt={featured.title}
                      title={`${featured.title} - 一本道影视大全`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded bg-accent-100 text-accent-800 font-medium">置顶</span>
                      <span className="px-2.5 py-1 rounded bg-secondary-100 text-secondary-800 font-medium">
                        {featured.category}
                      </span>
                      <span className="text-foreground-400">{featured.publishedAt}</span>
                    </div>
                    <h2 className="mt-4 font-heading text-xl md:text-2xl font-black text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {featured.title}
                    </h2>
                    <p className="mt-3 text-sm text-foreground-500 leading-relaxed line-clamp-3">{featured.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                      阅读全文
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            )}

            <div className="space-y-4">
              {rest.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/detail/${item.id}`}
                  className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-full sm:w-48 h-40 sm:h-28 rounded-md overflow-hidden shrink-0 bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 一本道影视大全`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded bg-secondary-100 text-secondary-800 font-medium">
                        {item.category}
                      </span>
                      <span className="text-foreground-400">{item.publishedAt}</span>
                    </div>
                    <h3 className="mt-3 text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  type="button"
                  disabled={safePage === 1}
                  onClick={() => setPage(safePage - 1)}
                  className="w-10 h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`w-10 h-10 rounded-md text-sm font-medium cursor-pointer ${
                      p === safePage
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={safePage === totalPages}
                  onClick={() => setPage(safePage + 1)}
                  className="w-10 h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}